import { useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { Seo } from '@/components/Seo'
import { AuthShell, FormError } from '@/components/AuthShell'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useSession } from '@/lib/session'
import { useI18n, translateError } from '@/i18n'
import { GoogleSignInButton } from '@/components/GoogleSignInButton'

/** CREATE ACCOUNT. Twin of Login.tsx. */
export default function Register() {
  const { register } = useSession()
  const { t } = useI18n()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const next = params.get('next') || '/account'

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [name, setName] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setBusy(true)
    try {
      await register(email, password, name || undefined)
      navigate(next, { replace: true })
    } catch (err) {
      setError(translateError(err, t, t('register.failed')))
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <Seo title={t('register.title')} noIndex />
      <AuthShell
        title={t('register.title')}
        subtitle={t('register.subtitle')}
        highlights={[
          t('register.highlight.firstIsAdmin'),
          t('register.highlight.noProvider'),
          t('register.highlight.selfServe'),
        ]}
        footer={
          <>
            {t('register.haveAccount')}{' '}
            <Link to="/login" className="font-medium text-foreground underline underline-offset-4">
              {t('login.submit')}
            </Link>
          </>
        }
      >
        <div data-upbo-src="src/pages/Register.tsx:61" className="flex flex-col gap-4">
          <FormError message={error} />
          <GoogleSignInButton
            label={t('auth.signUpWithGoogle')}
            onSuccess={() => navigate(next, { replace: true })}
            onError={(err) => setError(err)}
          />

          <div data-upbo-src="src/pages/Register.tsx:69" className="relative my-1">
            <div data-upbo-src="src/pages/Register.tsx:70" className="absolute inset-0 flex items-center">
              <div data-upbo-src="src/pages/Register.tsx:71" className="w-full border-t border-border" />
            </div>
            <div data-upbo-src="src/pages/Register.tsx:73" className="relative flex justify-center text-xs uppercase">
              <span data-upbo-src="src/pages/Register.tsx:74" className="bg-card px-2 text-muted-foreground">{t('auth.orDivider')}</span>
            </div>
          </div>

          <form data-upbo-src="src/pages/Register.tsx:78" onSubmit={submit} className="flex flex-col gap-4">
            <div data-upbo-src="src/pages/Register.tsx:79" className="flex flex-col gap-2">
              <Label htmlFor="email">{t('field.email')}</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('field.emailPlaceholder')}
              />
            </div>
            <div data-upbo-src="src/pages/Register.tsx:91" className="flex flex-col gap-2">
              <Label htmlFor="name">{t('field.displayName')}</Label>
              <Input
                id="name"
                type="text"
                autoComplete="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('field.optional')}
              />
            </div>
            <div data-upbo-src="src/pages/Register.tsx:102" className="flex flex-col gap-2">
              <Label htmlFor="password">{t('field.password')}</Label>
              <Input
                id="password"
                type="password"
                autoComplete="new-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <p data-upbo-src="src/pages/Register.tsx:112" className="text-xs text-muted-foreground">{t('register.passwordHint', { count: 8 })}</p>
            </div>
            <Button type="submit" disabled={busy} className="mt-2 w-full">
              {busy && <Loader2 className="size-4 animate-spin" />}
              {t('register.submit')}
            </Button>
          </form>
        </div>
      </AuthShell>
    </>
  )
}
